import React from "react"
import { twMerge } from "tailwind-merge"

import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { generateColorPalette } from "../utils/enhancedColorUtils"
import { getThemeShapeClasses } from "../utils/themeUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const CTASectionMobile = ({ section, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const layout = settings.layout || "content_centered"

  // Get theme colors with fallback generation
  const getThemeColors = () => {
    if (!theme?.colors) return { bgLight: "#FFFFFF", bgMedium: "#E6DAD7", bgExtraLight: "#F2EEEC" }

    const colors = { ...theme.colors }

    if (!colors.bgMedium || !colors.bgExtraLight) {
      const primaryColor = colors.primary || "#BD654F"
      const generated = generateColorPalette(primaryColor)
      colors.bgMedium = colors.bgMedium || generated.bgMedium
      colors.bgExtraLight = colors.bgExtraLight || generated.bgExtraLight
      colors.bgLight = colors.bgLight || generated.bgLight
    }

    return colors
  }

  const themeColors = getThemeColors()

  // Resolve background color based on toggle setting
  const resolveSectionBackgroundColor = () => {
    // Check if background color toggle is enabled (handle both boolean and string values)
    const showBgColor = settings.showBackgroundColor === true || settings.showBackgroundColor === "true"
    const bgColorIsTheme = settings.backgroundColor === "theme"

    if (showBgColor && bgColorIsTheme) {
      // Use bgLight from theme colors
      return themeColors.bgLight || theme?.colors?.bgLight || "#FFFFFF"
    }
    // Default to white (#FFF) if toggle is off or not set
    return "#FFF"
  }

  const backgroundColor = resolveSectionBackgroundColor()
  // Get highlights from settings with fallback to legacy highlights column
  const highlights = settings.highlights || section.highlights || []

  const renderContent = (textAlign = "center") => {
    // Use theme colors (removed custom color pickers)
    const titleColor = theme?.colors?.textDark || "#000000"
    const subheadlineColor = theme?.colors?.textDark || "#000000"
    const descriptionColor = theme?.colors?.textDark || "#000000"

    return (
      <div className={`text-${textAlign} space-y-3 md:space-y-4`}>
        {section.headline && (
          <h2
            className="text-[40px] font-bold leading-[48px]"
            style={{
              fontFamily: theme?.fonts?.heading || "serif",
              fontWeight: theme?.fonts?.headingWeight || "700",
              color: titleColor
            }}>
            {section.headline}
          </h2>
        )}
        {section.subheadline && (
          <p
            className="text-[27px] leading-[32px]"
            style={{
              fontFamily: theme?.fonts?.paragraph || "sans-serif",
              color: subheadlineColor
            }}>
            {section.subheadline}
          </p>
        )}
        {section.body && (
          <>
            <style>{`
              .cta-section-content ul {
                list-style-type: disc !important;
                margin-left: 1.5rem !important;
                margin-bottom: 1rem !important;
              }
              .cta-section-content li {
                margin-bottom: 0.5rem !important;
                display: list-item !important;
              }
              .cta-section-content ol {
                list-style-type: decimal !important;
                margin-left: 1.5rem !important;
                margin-bottom: 1rem !important;
              }
              .cta-section-content p {
                margin-bottom: 1rem !important;
              }
              .cta-section-content h1, .cta-section-content h2, .cta-section-content h3,
              .cta-section-content h4, .cta-section-content h5, .cta-section-content h6 {
                margin-bottom: 0.5rem !important;
                margin-top: 1rem !important;
              }
            `}</style>
            <div
              className="cta-section-content prose prose-lg w-full max-w-none text-[20px] leading-[32px]" // eslint-disable-line tailwindcss/no-custom-classname
              style={{
                fontFamily: theme?.fonts?.paragraph || "sans-serif",
                color: descriptionColor
              }}
              dangerouslySetInnerHTML={{ __html: section.body }}
            />
          </>
        )}
      </div>
    )
  }

  const renderHighlight = (highlight, index) => {
    // Use theme colors (removed custom color pickers)
    const titleColor = theme?.colors?.textDark || "#000000"
    const descriptionColor = theme?.colors?.textDark || "#000000"
    const highlightTitleFontSize = settings.highlightTitleFontSize || "18px"
    const highlightDescriptionFontSize = settings.highlightDescriptionFontSize || "14px"

    return (
      <div key={index} className="flex flex-col items-center space-y-2 text-center md:space-y-3">
        {highlight.imageUrl && (
          <div className="mb-2 md:mb-3">
            <img
              src={highlight.imageUrl}
              alt={highlight.title || `Highlight ${index + 1}`}
              className={twMerge("h-20 w-20 object-cover shadow-lg md:h-24 md:w-24", getThemeShapeClasses(theme))}
            />
          </div>
        )}
        {highlight.title && (
          <h3
            className="font-semibold leading-[22px] md:text-xl"
            style={{
              fontFamily: theme?.fonts?.heading || "serif",
              color: titleColor,
              fontSize: highlightTitleFontSize
            }}>
            {highlight.title}
          </h3>
        )}
        {highlight.description && (
          <div
            className="leading-[18px] md:text-base"
            style={{
              fontFamily: theme?.fonts?.paragraph || "sans-serif",
              color: descriptionColor,
              fontSize: highlightDescriptionFontSize
            }}
            dangerouslySetInnerHTML={{
              __html: typeof highlight.description === "object" ? highlight.description.html : highlight.description
            }}
          />
        )}
      </div>
    )
  }

  // Layout-specific rendering
  switch (layout) {
    case "content_centered":
      return (
        <section
          className={twMerge("relative py-8 md:py-12", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor }}>
          <div className="mx-auto max-w-3xl px-6">
            <AnimatedSection>
              {section.imageUrl && (
                <div className="mb-6 flex justify-center">
                  <div className="relative">
                    <img
                      src={section.imageUrl}
                      alt={section.headline || "CTA image"}
                      className={twMerge("relative h-48 w-48 object-cover", getThemeShapeClasses(theme))}
                    />
                  </div>
                </div>
              )}
              {renderContent("left")}
            </AnimatedSection>
          </div>
        </section>
      )

    case "image_right":
      return (
        <section
          className={twMerge("relative py-8 md:py-12", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor }}>
          <div className="mx-auto max-w-6xl px-4 md:px-4">
            <div className="flex flex-col items-center gap-6 md:gap-8">
              <AnimatedSection>{renderContent("center")}</AnimatedSection>
              {section.imageUrl && (
                <div className="relative w-full">
                  <img
                    src={section.imageUrl}
                    alt={section.headline || "CTA image"}
                    className={twMerge("h-auto w-full shadow-xl", getThemeShapeClasses(theme))}
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      )

    case "image_left":
      return (
        <section
          className={twMerge("relative py-8 md:py-12", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor }}>
          <div className="mx-auto max-w-6xl px-4 md:px-4">
            <div className="flex flex-col items-center gap-6 md:gap-8">
              {section.imageUrl && (
                <div className="relative w-full">
                  <img
                    src={section.imageUrl}
                    alt={section.headline || "CTA image"}
                    className={twMerge("h-auto w-full shadow-xl", getThemeShapeClasses(theme))}
                  />
                </div>
              )}
              <AnimatedSection>{renderContent("center")}</AnimatedSection>
            </div>
          </div>
        </section>
      )

    case "half_image_right":
      return (
        <section
          className={twMerge("relative py-8 md:py-12", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor }}>
          <div className="mx-auto max-w-3xl px-6">
            <div className="relative flex flex-col items-center gap-4 md:gap-6">
              <AnimatedSection
                className={twMerge("w-full", getThemeShapeClasses(theme))}
                style={{ backgroundColor: theme?.colors?.bgLight || "#FFFFFF" }}>
                <div className="p-6 md:p-8">{renderContent("left")}</div>
              </AnimatedSection>
              {section.imageUrl && (
                <div className="relative w-full">
                  <img
                    src={section.imageUrl}
                    alt={section.headline || "CTA image"}
                    className={twMerge("h-auto w-full shadow-xl", getThemeShapeClasses(theme))}
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      )

    case "half_image_left":
      return (
        <section
          className={twMerge("relative py-8 md:py-12", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor }}>
          <div className="mx-auto max-w-3xl px-6">
            <div className="relative flex flex-col items-center gap-4 md:gap-6">
              {section.imageUrl && (
                <div className="relative w-full">
                  <img
                    src={section.imageUrl}
                    alt={section.headline || "CTA image"}
                    className={twMerge("h-auto w-full shadow-xl", getThemeShapeClasses(theme))}
                  />
                </div>
              )}
              <AnimatedSection
                className={twMerge("w-full", getThemeShapeClasses(theme))}
                style={{ backgroundColor: theme?.colors?.bgLight || "#FFFFFF" }}>
                <div className="p-6 md:p-8">{renderContent("left")}</div>
              </AnimatedSection>
            </div>
          </div>
        </section>
      )

    case "highlights_2":
      return (
        <section
          className={twMerge("relative py-8 md:py-12", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor }}>
          <div className="mx-auto max-w-3xl px-6">
            <AnimatedSection className="mb-6 md:mb-8">{renderContent("center")}</AnimatedSection>
            <div className="grid w-full grid-cols-2 gap-4 lg:gap-6">
              {highlights.slice(0, 2).map((highlight, index) => renderHighlight(highlight, index))}
            </div>
          </div>
        </section>
      )

    case "highlights_3":
      return (
        <section
          className={twMerge("relative py-8 md:py-12", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor }}>
          <div className="mx-auto max-w-3xl px-6">
            <AnimatedSection className="mb-6 md:mb-8">{renderContent("center")}</AnimatedSection>
            <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
              {highlights.slice(0, 3).map((highlight, index) => renderHighlight(highlight, index))}
            </div>
          </div>
        </section>
      )

    default:
      return (
        <section
          className={twMerge("relative py-8 md:py-12", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor }}>
          <div className="mx-auto max-w-3xl px-6">
            <AnimatedSection>{renderContent("center")}</AnimatedSection>
          </div>
        </section>
      )
  }
}

export default CTASectionMobile
